/* @flow */
/* eslint-env es6,browser */

import adconfig from './adconfig';
import { BrowserUtil } from './utils/browserutil';
import { adcontainer } from './adcontainer';
import { cachedAdcontainer } from './cachedcontainer';

/**
 * @description Helper class to work with container
 * @exports containerHelper
 * @requires  module:adconfig
 * @requires  module:browserutil
 * @requires module:adcontainer
 * @requires module:cachedAdcontainer
 */

class _containerHelper {
  _rotationList: Array<string>;
  constructor() {
    this._rotationList = [];
  }

  /**
   * Initialize the object
   *
   * @typedef {object} globalParams
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    [adcontainer, cachedAdcontainer].forEach(c => c.init(g));
  }

  /**
   * Update/store ad rotation list for helper class
   *
   * @param {Array<string>} list - List of ad rotation positions
   * @returns {undefined}
   */
  setRotationList(list: Array<string> = []) {
    this._rotationList = list;
  }

  /**
   * @description Returns adInfo object for a given position
   * @private
   * @typedef {object} adInfo
   * @param {string} position - position name
   * @returns {adInfo} - AdInfo object
   */
  _getAdInfoObjectFromPosition(position: string = ''): adInfo {
    const _ret: adInfo = {
      id: position,
      content: '',
      customVars: {},
      adRotation: false,
      cID: '',
      fID: '',
      adServer: '',
      addBodyTag: false,
      as: adconfig.getPosSettings(position)
    };
    return _ret;
  }

  /**
   * Caching is enabled only for adrotation position, iframe ad and non old IE
   *
   * @param {string} adPos - ad position
   * @param {boolean} isIframeAd - is loaded inside IFrame?
   * @returns {boolean} - Caching status
   */
  isCachingEnabled(adPos: string, isIframeAd: boolean | void) {
    return (
      this._rotationList.includes(adPos) &&
      isIframeAd !== false &&
      !BrowserUtil.isOldIE()
    );
  }

  /**
   * Get container object based on ad rotation status
   *
   * @typedef {object} _adContainer
   * @param {string} adPos - Ad Position
   * @returns {_adContainer} - Container Object
   */
  getContainer(adPos: string = '') {
    return adPos.length &&
      this.isCachingEnabled(adPos, adconfig.getPosSettings(adPos).iframe)
      ? cachedAdcontainer
      : adcontainer;
  }

  /**
   * Ensure that containers are created for ad rotation position
   *
   * @param {string} pos - ad position
   * @returns {undefined}
   */
  createContainers(pos: string = '') {
    if (pos.length) {
      const cont = this.getContainer(pos);
      cont.addContainerDivs(pos);
    }
  }

  /**
   * Gets ad position's container div and frame info
   *
   * @param {string} position - ad position
   * @param {boolean} bActive - Active/Inactive
   * @returns {adInfo} - Frame and container id
   */
  getAdContainerInfo(position: string = '', bActive?: boolean = false): adInfo {
    const AdInfo: adInfo = this._getAdInfoObjectFromPosition(position);

    if (!position.length) {
      return AdInfo;
    }

    const cont = this.getContainer(position);
    cont._updateAdContainerInfo(AdInfo, bActive);
    return AdInfo;
  }

  /**
   * @description Get position name from div/iframe ID
   * @param {string} id - Div/iFrame ID
   * @returns {string} Non-cached position name
   */
  getNonCachedPosName(id: string = ''): string {
    let ret = '';
    if (id.length) {
      ret = cachedAdcontainer.getNonCachedPosName(id);
    }
    return ret;
  }

  getAdContainer() {
    return adcontainer;
  }
  getCachedContainer() {
    return cachedAdcontainer;
  }
}

export default new _containerHelper();
